import {Box, Divider, Stack, TextField, Typography} from '@mui/material';
import {FixedSizeList as Lists} from 'react-window';
import type {CSSProperties, ReactNode} from 'react';
import {
  formatTnnCurrency,
  getPartnerShare,
  getPartnerName,
  getTnnPartnerAmount,
  ITnnPartnerAmountPartner,
  sumPartnerAmount,
  sumPartnerShare,
  TNN_PARTNER_AMOUNT_FIELDS,
  TnnPartnerAmountOptions,
} from './tnnPartnerUtils';

const partnerAmountGridColumns = {
  xs: 'minmax(0, 1fr) 90px 90px',
  sm: 'minmax(0, 1fr) 100px 100px',
};

const PartnerAmountGrid = ({
  children,
  variant = 'header',
}: {
  children: ReactNode;
  variant?: 'header' | 'row' | 'total';
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: partnerAmountGridColumns,
      gap: 1.5,
      alignItems: 'center',
      px: 3,
      ...(variant === 'row' ? {minHeight: 48, py: 0.5} : {py: 1.5}),
      ...(variant === 'total'
        ? {
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'customBgColors.blue_variant6',
            fontWeight: 600,
          }
        : {}),
    }}>
    {children}
  </Box>
);

type PartnerAmountRowData = {
  partners: ITnnPartnerAmountPartner[];
  editable: boolean;
  onPriceChange?: (companyId: number, value: string) => void;
  emptyValue: string;
  options: TnnPartnerAmountOptions;
  calculateShareWhenMissing: boolean;
  currencyMinimumFractionDigits: number;
};

type PartnerAmountRowProps = {
  index: number;
  style: CSSProperties;
  data: PartnerAmountRowData;
};

const PartnerAmountRow = ({index, style, data}: PartnerAmountRowProps) => {
  const partner = data.partners[index];
  if (!partner) return null;

  const name = getPartnerName(partner);
  const price = getTnnPartnerAmount(
    partner,
    TNN_PARTNER_AMOUNT_FIELDS.price,
    data.options,
  );
  const share = data.calculateShareWhenMissing
    ? getPartnerShare(partner, undefined, data.options)
    : getTnnPartnerAmount(
        partner,
        TNN_PARTNER_AMOUNT_FIELDS.share,
        data.options,
      );

  return (
    <Box style={{...style, paddingBottom: 8}}>
      <PartnerAmountGrid variant='row'>
        <Typography noWrap title={name} sx={{minWidth: 0}}>
          {name || '—'}
        </Typography>
        {data.editable ? (
          <TextField
            size='small'
            type='number'
            value={price ?? ''}
            onChange={event =>
              data.onPriceChange?.(Number(partner.id), event.target.value)
            }
            inputProps={{
              min: 0,
              step: 0.01,
              'aria-label': `Partner Price for ${name || partner.id}`,
            }}
            sx={{
              width: 100,
              '& .MuiInputBase-root': {height: 36},
            }}
          />
        ) : (
          <Typography>
            {formatTnnCurrency(
              price,
              data.emptyValue,
              data.currencyMinimumFractionDigits,
            )}
          </Typography>
        )}
        <Typography noWrap>
          {formatTnnCurrency(
            share,
            data.emptyValue,
            data.currencyMinimumFractionDigits,
          )}
        </Typography>
      </PartnerAmountGrid>
      <Divider sx={{borderColor: 'divider'}} />
    </Box>
  );
};

export type TnnPartnerAmountTableProps = {
  title: string;
  partners: ITnnPartnerAmountPartner[];
  editable?: boolean;
  onPriceChange?: (companyId: number, value: string) => void;
  listHeight?: number;
  emptyLabel?: string;
  emptyValue?: string;
  preferPivot?: boolean;
  calculateShareWhenMissing?: boolean;
  currencyMinimumFractionDigits?: number;
};

export const TnnPartnerAmountTotals = ({
  price,
  share,
  boxed = false,
  emptyValue = '$ 0.00',
  currencyMinimumFractionDigits = 2,
}: {
  price: number | string | null;
  share: number | string | null;
  boxed?: boolean;
  emptyValue?: string;
  currencyMinimumFractionDigits?: number;
}) => (
  <Stack
    sx={
      boxed
        ? {
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3.5,
            bgcolor: 'background.default',
          }
        : undefined
    }>
    <PartnerAmountGrid variant='total'>
      <Typography>Total</Typography>
      <Typography>
        {formatTnnCurrency(price, emptyValue, currencyMinimumFractionDigits)}
      </Typography>
      <Typography>
        {formatTnnCurrency(share, emptyValue, currencyMinimumFractionDigits)}
      </Typography>
    </PartnerAmountGrid>
  </Stack>
);

export const TnnPartnerAmountTable = ({
  title,
  partners,
  editable = false,
  onPriceChange,
  listHeight,
  emptyLabel = 'No partners selected.',
  emptyValue = '—',
  preferPivot = false,
  calculateShareWhenMissing = true,
  currencyMinimumFractionDigits = 2,
}: TnnPartnerAmountTableProps) => {
  const height =
    listHeight ?? Math.min(320, Math.max(64, partners.length * 64));
  const options = {preferPivot, calculateShareWhenMissing};
  const totalPrice = sumPartnerAmount(
    partners,
    TNN_PARTNER_AMOUNT_FIELDS.price,
    options,
  );
  const totalShare = sumPartnerShare(partners, options);

  return (
    <Stack
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3.5,
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}>
      <PartnerAmountGrid>
        <Typography noWrap sx={{color: 'text.secondary', fontSize: 14}}>
          {title} ({partners.length})
        </Typography>
        <Typography sx={{color: 'text.secondary', fontSize: 14}}>
          Partner Price
        </Typography>
        <Typography sx={{color: 'text.secondary', fontSize: 14}}>
          Partner Share
        </Typography>
      </PartnerAmountGrid>
      <Divider sx={{borderColor: 'divider'}} />
      {partners.length > 0 ? (
        <Lists
          height={height}
          itemCount={partners.length}
          itemSize={64}
          itemKey={(index: number) => partners[index]?.id ?? index}
          width='100%'
          className='scrollbar'
          itemData={{
            partners,
            editable,
            onPriceChange,
            emptyValue,
            options,
            calculateShareWhenMissing,
            currencyMinimumFractionDigits,
          }}>
          {PartnerAmountRow}
        </Lists>
      ) : (
        <Typography sx={{px: 3, py: 2}} color='text.secondary'>
          {emptyLabel}
        </Typography>
      )}
      <TnnPartnerAmountTotals
        price={totalPrice}
        share={totalShare}
        emptyValue={emptyValue}
        currencyMinimumFractionDigits={currencyMinimumFractionDigits}
      />
    </Stack>
  );
};
